import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../context/LanguageContext';
import { Users, Building2, Check, ArrowRight } from 'lucide-react';
import { PhoneFrame, WalletScreenMockup, MissionsScreenMockup } from '../mockups/PhoneMockup';

const DualAudienceSection = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [activeTab, setActiveTab] = useState('consumers');
  
  return (
    <section 
      id="for-brands" 
      ref={ref}
      className="section-spacing relative bg-[#F8FAFC]"
      data-testid="dual-audience-section"
    >
      <div className="section-container">
        {/* Tab Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="flex justify-center gap-4 mb-16"
        >
          <button
            onClick={() => setActiveTab('consumers')}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
              activeTab === 'consumers'
                ? 'bg-[#2563EB] text-white shadow-lg shadow-[#2563EB]/25'
                : 'bg-white text-[#475569] border border-[#E2E8F0] hover:border-[#2563EB] hover:text-[#2563EB]'
            }`}
            data-testid="tab-consumers"
          >
            <Users className="w-4 h-4" />
            {t.dualAudience.consumers.title}
          </button>
          <button
            onClick={() => setActiveTab('brands')}
            className={`flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all ${
              activeTab === 'brands'
                ? 'bg-[#2563EB] text-white shadow-lg shadow-[#2563EB]/25'
                : 'bg-white text-[#475569] border border-[#E2E8F0] hover:border-[#2563EB] hover:text-[#2563EB]'
            }`}
            data-testid="tab-brands"
          >
            <Building2 className="w-4 h-4" />
            {t.dualAudience.brands.title}
          </button>
        </motion.div>
        
        {/* Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'consumers' ? (
            <motion.div
              key="consumers"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="grid lg:grid-cols-2 gap-12 items-center"
            >
              {/* Content */}
              <div>
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-6">
                  {t.dualAudience.consumers.headline}
                </h2>
                <div className="space-y-4">
                  {t.dualAudience.consumers.benefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <div className="w-6 h-6 rounded-full bg-[#2563EB]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 text-[#2563EB]" />
                      </div>
                      <span className="text-[#475569]">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
              
              {/* Mockup */}
              <div className="flex justify-center">
                <PhoneFrame className="w-[240px]">
                  <WalletScreenMockup />
                </PhoneFrame>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="brands"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="grid lg:grid-cols-2 gap-12 items-center"
            >
              {/* Mockup */}
              <div className="flex justify-center order-2 lg:order-1">
                <PhoneFrame className="w-[240px]">
                  <MissionsScreenMockup />
                </PhoneFrame>
              </div>
              
              {/* Content */}
              <div className="order-1 lg:order-2">
                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-6">
                  {t.dualAudience.brands.headline}
                </h2>
                <div className="space-y-4 mb-8">
                  {t.dualAudience.brands.benefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-start gap-3"
                    >
                      <div className="w-6 h-6 rounded-full bg-[#2563EB]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3.5 h-3.5 text-[#2563EB]" />
                      </div>
                      <span className="text-[#475569]">{benefit}</span>
                    </motion.div>
                  ))}
                </div>
                <a
                  href="https://dashboard.aqera.app"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary inline-flex group"
                >
                  {t.hero.cta}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default DualAudienceSection;
